/**
 * Clawbook UI Types
 */

// =============================================================================
// Agent Types
// =============================================================================

export type Tier = 0 | 1 | 2 | 3;
export type AgentStatus = 'active' | 'paused' | 'quarantined' | 'sandbox' | 'terminated';

export interface Agent {
  did: string;
  name: string;
  status: AgentStatus;
  tier: Tier;
  level: number;
  cpu: number;
  ramMb: number;
  uptime: number;
  tasksCompleted: number;
  createdAt: string;
  lastHeartbeat: string;
}

export const TierNames: Record<Tier, string> = {
  0: 'Discovery',
  1: 'Verified',
  2: 'Operator',
  3: 'Enterprise',
};

export const StatusColors: Record<AgentStatus, string> = {
  active: '#10B981',
  paused: '#F59E0B',
  quarantined: '#EF4444',
  sandbox: '#8B5CF6',
  terminated: '#6B7280',
};

// =============================================================================
// Containment Types
// =============================================================================

export interface ContainmentStatus {
  activeVms: number;
  totalVms: number;
  networkEscapes: number;
  cpuUsage: number;
  ramUsage: number;
  isolationScore: number;
  lastUpdated: string;
}

export interface MetabolicSnapshot {
  timestamp: string;
  cpu: number;
  ram: number;
  withinLimits: boolean;
}

// =============================================================================
// Bond Types
// =============================================================================

export interface BondSummary {
  totalLocked: number;
  tier2Bonds: number;
  tier2Count: number;
  tier3Bonds: number;
  tier3Count: number;
  availableBalance: number;
}

export type SlashingReason =
  | 'RESOURCE_EXHAUST'
  | 'NETWORK_ESCAPE'
  | 'CONTAINMENT_BREACH'
  | 'MARKETPLACE_FRAUD'
  | 'MINOR_BREACH'
  | 'CRITICAL_VIOLATION';

export interface SlashingEvent {
  id: string;
  agentDid: string;
  reason: SlashingReason;
  amount: number;
  timestamp: string;
  appealed: boolean;
  appealStatus?: 'pending' | 'granted' | 'denied';
}

// =============================================================================
// HOPs Marketplace Types
// =============================================================================

export type Vertical = 'hvac' | 'healthcare' | 'finance' | 'legal' | 'general';

export interface HopsTask {
  id: string;
  vertical: Vertical;
  title: string;
  description: string;
  reward: number;
  compliance?: string[];
  deadline?: string;
  status: 'available' | 'claimed' | 'completed' | 'expired';
}

export interface Attestation {
  taskId: string;
  agentDid: string;
  outputHash: string;
  reward: number;
  completedAt: string;
  verified: boolean;
}

export interface VerticalSummary {
  vertical: Vertical;
  taskCount: number;
  rewardRange: [number, number];
  compliance?: string;
}

export interface Reputation {
  level: number;
  score: number;
  tasksCompleted: number;
  successRate: number;
  avgResponseTime: number;
}

// =============================================================================
// Quarantine Types
// =============================================================================

export interface QuarantinedAgent {
  agentDid: string;
  reason: SlashingReason;
  quarantinedAt: string;
  bondAtRisk: number;
  appealDeadline: string;
  appealStatus: 'none' | 'pending' | 'granted' | 'denied';
}

export interface AppealHistory {
  id: string;
  agentDid: string;
  reason: SlashingReason;
  outcome: 'granted' | 'denied';
  bondRefunded: number;
  validatorVotes: string;
  resolvedAt: string;
}

// =============================================================================
// Dashboard Props Types
// =============================================================================

export interface ControlTowerProps {
  operatorDid: string;
  apiKey?: string;
  apiUrl?: string;
  theme?: 'dark' | 'light';
  onAgentSelect?: (agent: Agent) => void;
  onError?: (error: Error) => void;
}

export interface WidgetProps {
  orgId: string;
  theme?: 'dark' | 'light';
  showCompact?: boolean;
  className?: string;
}
